// src/pages/KDS/components/KDSOrderCard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertCircle, ChevronDown, ChevronUp, Play, Check, Flame } from 'lucide-react';

// Time to keep showing ready items before hiding (in milliseconds)
const READY_DISPLAY_DURATION = 20 * 1000; // 20 seconds
const READY_TIMESTAMPS_KEY = 'kds-ready-timestamps';

// Helper to get ready timestamps from localStorage
const getStoredReadyTimestamps = () => {
  try {
    const stored = localStorage.getItem(READY_TIMESTAMPS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Helper to save ready timestamps to localStorage
const saveReadyTimestamps = (timestamps) => {
  try {
    // Clean up old timestamps (older than 1 minute to avoid stale data buildup)
    const now = Date.now();
    const cleaned = {};
    Object.entries(timestamps).forEach(([id, time]) => {
      if (now - time < 60000) { // Keep for 1 minute max
        cleaned[id] = time;
      }
    });
    localStorage.setItem(READY_TIMESTAMPS_KEY, JSON.stringify(cleaned));
  } catch {
    // Ignore storage errors
  }
};

// Time thresholds (in seconds)
const TIME_WARNING = 5 * 60;   // 5 minutes
const TIME_CRITICAL = 10 * 60; // 10 minutes

// Item status configuration
// KDS only handles Start (PENDING→PREPARING) and Ready (PREPARING→READY)
// SERVED status is marked by waitstaff from a different interface
// Status is shown via text/quantity badge colors - no separate status text needed
const ITEM_STATUS_CONFIG = {
  PENDING: {
    itemTextColor: 'text-slate-200',
    quantityBg: 'bg-slate-600',
    nextStatus: 'PREPARING',
    nextIcon: Play,
    nextTooltip: 'Start Cooking',
    nextBg: 'bg-teal-500 hover:bg-teal-400',
  },
  PREPARING: {
    itemTextColor: 'text-amber-500',
    quantityBg: 'bg-amber-500',
    nextStatus: 'READY',
    nextIcon: Check,
    nextTooltip: 'Mark Ready',
    nextBg: 'bg-emerald-500 hover:bg-emerald-400',
  },
  READY: {
    itemTextColor: 'text-emerald-400',
    quantityBg: 'bg-emerald-600',
    nextStatus: null,
    nextIcon: null,
    nextTooltip: null,
    nextBg: null,
  },
  SERVED: {
    itemTextColor: 'text-slate-500 line-through',
    quantityBg: 'bg-slate-600',
    nextStatus: null,
    nextIcon: null,
    nextTooltip: null,
    nextBg: null,
  },
};

// Order status colors
const ORDER_STATUS_COLORS = {
  SENT: 'border-l-teal-500',
  PREPARING: 'border-l-amber-500',
  READY: 'border-l-emerald-500',
  SERVED: 'border-l-slate-500',
};

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getTimerColor(seconds, orderStatus) {
  if (orderStatus === 'READY' || orderStatus === 'SERVED') return 'text-emerald-400';
  if (seconds >= TIME_CRITICAL) return 'text-red-400';
  if (seconds >= TIME_WARNING) return 'text-amber-400';
  return 'text-slate-400';
}

// Size configuration for different layout views
const SIZE_CONFIG = {
  compact: {
    maxHeight: 'max-h-[280px]',
    headerPadding: 'px-2 py-1.5',
    headerText: 'text-xs',
    timerSize: 10,
    kotHeaderPadding: 'px-2 py-1',
    kotText: 'text-[10px]',
    itemPadding: 'px-2 py-1',
    itemText: 'text-xs leading-tight',
    variantText: 'text-[10px]',
    quantitySize: 'w-5 h-5 text-[10px]',
    buttonSize: 'p-1.5',
    buttonIconSize: 14,
    chevronSize: 12,
  },
  normal: {
    maxHeight: 'max-h-[400px]',
    headerPadding: 'px-3 py-2',
    headerText: 'text-sm',
    timerSize: 12,
    kotHeaderPadding: 'px-3 py-1.5',
    kotText: 'text-xs',
    itemPadding: 'px-3 py-2',
    itemText: 'text-sm',
    variantText: 'text-xs',
    quantitySize: 'w-6 h-6 text-xs',
    buttonSize: 'p-2',
    buttonIconSize: 16,
    chevronSize: 14,
  },
  wide: {
    maxHeight: 'max-h-[500px]',
    headerPadding: 'px-4 py-3',
    headerText: 'text-base',
    timerSize: 14,
    kotHeaderPadding: 'px-4 py-2',
    kotText: 'text-sm',
    itemPadding: 'px-4 py-3',
    itemText: 'text-base',
    variantText: 'text-sm',
    quantitySize: 'w-7 h-7 text-sm',
    buttonSize: 'p-2.5',
    buttonIconSize: 18,
    chevronSize: 16,
  }
};

/**
 * KDS Table Card - Shows all KOTs for a table grouped together
 * Each item has action buttons to change its status
 * @param {boolean} showReadyItems - When false, hides items with READY status
 * @param {string} cardSize - 'compact', 'normal', or 'wide'
 */
export default function KDSOrderCard({ table, onUpdateItemStatus, showReadyItems = false, cardSize = 'normal' }) {
  const [elapsed, setElapsed] = useState(0);
  const [expandedKots, setExpandedKots] = useState({});
  const [, forceUpdate] = useState(0); // For triggering re-renders

  // Track previous item statuses to detect transitions
  const prevItemStatuses = useRef({});

  const kots = table.kots || [];
  const sizeConfig = SIZE_CONFIG[cardSize] || SIZE_CONFIG.normal;

  // Track items that transition to READY status and persist to localStorage
  useEffect(() => {
    const allItems = kots.flatMap(kot => kot.items || kot.orderItems || []);
    const storedTimestamps = getStoredReadyTimestamps();
    let hasChanges = false;

    allItems.forEach(item => {
      const currentStatus = (item.itemStatus || 'PENDING').toUpperCase();
      const prevStatus = prevItemStatuses.current[item.id];

      // If item just became READY (status changed to READY), record timestamp
      if (currentStatus === 'READY' && prevStatus && prevStatus !== 'READY') {
        storedTimestamps[item.id] = Date.now();
        hasChanges = true;

        // Schedule re-render after READY_DISPLAY_DURATION to hide the item
        setTimeout(() => {
          forceUpdate(n => n + 1);
        }, READY_DISPLAY_DURATION + 100);
      }

      // Update previous status
      prevItemStatuses.current[item.id] = currentStatus;
    });

    // Save to localStorage if there were changes
    if (hasChanges) {
      saveReadyTimestamps(storedTimestamps);
    }
  }, [kots]);

  // Initialize previous statuses on first render (without recording timestamps)
  useEffect(() => {
    const allItems = kots.flatMap(kot => kot.items || kot.orderItems || []);
    allItems.forEach(item => {
      if (!prevItemStatuses.current[item.id]) {
        prevItemStatuses.current[item.id] = (item.itemStatus || 'PENDING').toUpperCase();
      }
    });
  }, []);

  // Filter items based on showReadyItems flag and ready duration (from localStorage)
  const filterItems = (items) => {
    if (showReadyItems) return items;

    const storedTimestamps = getStoredReadyTimestamps();
    const now = Date.now();

    return items.filter(item => {
      const status = (item.itemStatus || 'PENDING').toUpperCase();

      // Always hide served items
      if (status === 'SERVED') return false;

      // Show non-ready items
      if (status !== 'READY') return true;

      // For READY items, check if within display duration (from localStorage)
      const readyTime = storedTimestamps[item.id];
      if (readyTime) {
        const elapsed = now - readyTime;
        return elapsed < READY_DISPLAY_DURATION;
      }

      // If no timestamp in localStorage (was already ready before tracking), hide it
      return false;
    });
  };

  // Calculate elapsed time from oldest KOT
  useEffect(() => {
    if (kots.length === 0) return;
    const oldestTime = Math.min(...kots.map(k => new Date(k.createdAt).getTime()));
    const update = () => setElapsed(Math.floor((Date.now() - oldestTime) / 1000));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [kots]);

  // Initialize all KOTs as expanded
  useEffect(() => {
    const initial = {};
    kots.forEach(kot => { initial[kot.id] = true; });
    setExpandedKots(initial);
  }, [kots.length]);

  // Get overall status (highest priority status among all KOTs)
  const getOverallStatus = () => {
    const statuses = kots.map(k => k.orderStatus || k.status || 'SENT');
    if (statuses.includes('PREPARING')) return 'PREPARING';
    if (statuses.includes('READY')) return 'READY';
    if (statuses.every(s => s === 'SERVED')) return 'SERVED';
    return 'SENT';
  };

  const overallStatus = getOverallStatus();
  const borderColor = ORDER_STATUS_COLORS[overallStatus] || ORDER_STATUS_COLORS.SENT;
  const isDelayed = elapsed >= TIME_WARNING && overallStatus !== 'READY' && overallStatus !== 'SERVED';

  // Toggle KOT expansion
  const toggleKot = (kotId) => {
    setExpandedKots(prev => ({ ...prev, [kotId]: !prev[kotId] }));
  };

  // Handle item status update
  const handleItemStatusChange = (orderId, itemId, newStatus) => {
    if (onUpdateItemStatus) {
      onUpdateItemStatus(orderId, itemId, newStatus);
    }
  };

  // Check if this card has any visible items (must be after hooks)
  const hasVisibleItems = kots.some(kot => {
    const items = kot.items || kot.orderItems || [];
    return filterItems(items).length > 0;
  });

  // Don't render card if no visible items
  if (!hasVisibleItems) {
    return null;
  }

  return (
    <div className={`bg-slate-800 rounded-lg border-l-4 ${borderColor} overflow-hidden shadow-lg`}>
      {/* Table Header */}
      <div className={`bg-slate-700 ${sizeConfig.headerPadding} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <h3 className={`${sizeConfig.headerText} font-bold text-white`}>
            {table.tableName}
            {table.tableSplit && table.tableSplit !== 'A' && (
              <span className="ml-1 text-slate-400">({table.tableSplit})</span>
            )}
          </h3>
          <span className={`${sizeConfig.kotText} text-slate-400`}>
            {kots.length} KOT{kots.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className={`flex items-center gap-1 ${sizeConfig.kotText} font-mono ${getTimerColor(elapsed, overallStatus)}`}>
          {isDelayed && <AlertCircle size={sizeConfig.timerSize} />}
          <Clock size={sizeConfig.timerSize} />
          <span>{formatTime(elapsed)}</span>
        </div>
      </div>

      {/* KOTs - Custom scrollbar */}
      <div className={`${sizeConfig.maxHeight} overflow-y-auto kds-scrollbar`}>
        {kots.map((kot, kotIndex) => {
          const allItems = kot.items || kot.orderItems || [];
          const visibleItems = filterItems(allItems);
          const kotStatus = kot.orderStatus || kot.status || 'SENT';
          const isExpanded = expandedKots[kot.id] !== false;

          // Skip KOT if no visible items
          if (visibleItems.length === 0) return null;

          return (
            <div key={kot.id} className={kotIndex > 0 ? 'border-t border-slate-600' : ''}>
              {/* KOT Header */}
              <button
                onClick={() => toggleKot(kot.id)}
                className={`w-full ${sizeConfig.kotHeaderPadding} bg-slate-750 hover:bg-slate-700 flex items-center justify-between text-left`}
              >
                <div className="flex items-center gap-1.5">
                  <span className={`${sizeConfig.kotText} font-medium text-slate-300`}>
                    #{kot.kotNumber || kot.id}
                  </span>
                  <span className={`${sizeConfig.kotText} px-1.5 py-0.5 rounded ${
                    kotStatus === 'PREPARING' ? 'bg-amber-500/20 text-amber-400' :
                    kotStatus === 'READY' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-slate-600 text-slate-400'
                  }`}>
                    {kotStatus}
                  </span>
                  {/* Show hidden count if some items are filtered */}
                  {!showReadyItems && allItems.length > visibleItems.length && (
                    <span className={`${sizeConfig.kotText} text-slate-500`}>
                      +{allItems.length - visibleItems.length} ready
                    </span>
                  )}
                </div>
                {isExpanded ? <ChevronUp size={sizeConfig.chevronSize} className="text-slate-400" /> : <ChevronDown size={sizeConfig.chevronSize} className="text-slate-400" />}
              </button>

              {/* Items */}
              {isExpanded && (
                <div className="bg-slate-800">
                  {visibleItems.map((item, itemIdx) => {
                    const itemStatus = (item.itemStatus || 'PENDING').toUpperCase();
                    const statusConfig = ITEM_STATUS_CONFIG[itemStatus] || ITEM_STATUS_CONFIG.PENDING;

                    return (
                      <div
                        key={item.id || itemIdx}
                        className={`flex items-center gap-2 ${sizeConfig.itemPadding} border-b border-slate-700/50 ${
                          itemStatus === 'SERVED' ? 'opacity-40' : ''
                        }`}
                      >
                        {/* Quantity badge with flame overlay for cooking items */}
                        <span className={`relative flex-shrink-0 ${sizeConfig.quantitySize} rounded flex items-center justify-center font-bold text-white ${statusConfig.quantityBg}`}>
                          {item.quantity}
                          {/* Animated Flame overlay for PREPARING items */}
                          {itemStatus === 'PREPARING' && (
                            <Flame
                              size={12}
                              className="absolute -top-1.5 -right-1.5 text-amber-500 animate-flame"
                            />
                          )}
                        </span>

                        {/* Item Name - color indicates status, no truncation */}
                        <div className="flex-1 min-w-0">
                          <div className={`${sizeConfig.itemText} font-medium ${statusConfig.itemTextColor}`}>
                            {item.itemName || item.menuItemName || item.name}
                          </div>
                          {(item.variantName || item.variant) && (
                            <div className={`${sizeConfig.variantText} ${itemStatus === 'PREPARING' ? 'text-amber-500/70' : itemStatus === 'READY' ? 'text-emerald-400/70' : 'text-slate-400'}`}>
                              {item.variantName || item.variant}
                            </div>
                          )}
                          {(item.specialInstructions || item.notes) && (
                            <div className={`${sizeConfig.variantText} text-amber-400 mt-0.5 italic`}>
                              ⚠ {item.specialInstructions || item.notes}
                            </div>
                          )}
                        </div>

                        {/* Action Button - Icon only with tooltip */}
                        {statusConfig.nextStatus && statusConfig.nextIcon && (
                          <button
                            onClick={() => handleItemStatusChange(kot.id, item.id, statusConfig.nextStatus)}
                            className={`flex-shrink-0 ${sizeConfig.buttonSize} rounded-lg text-white transition-all duration-200 shadow-sm hover:shadow-md active:scale-95 ${statusConfig.nextBg}`}
                            title={statusConfig.nextTooltip}
                          >
                            <statusConfig.nextIcon size={sizeConfig.buttonIconSize} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
